import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, KeyRound } from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_aestha-hub/artifacts/58pjy83g_IMG_0746.jpeg";
const AUTH_IMAGE = "https://customer-assets.emergentagent.com/job_aestha-hub/artifacts/1y57ncte_LOGIN.webp";

export const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  // Password reset states
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [resetting, setResetting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(email, password);
      toast.success("Login realizado com sucesso!");
      
      // Use window.location for more reliable navigation after login
      // This ensures a clean state without React Router race conditions
      const targetUrl = user.role === "admin" ? "/admin" : "/dashboard";
      window.location.href = targetUrl;
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao fazer login";
      toast.error(message);
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      toast.error("As senhas não coincidem");
      return;
    }
    
    setResetting(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: resetEmail,
        code: resetCode,
        new_password: newPassword
      });
      toast.success("Senha alterada com sucesso! Você já pode fazer login.");
      setShowResetDialog(false);
      setResetEmail("");
      setResetCode("");
      setNewPassword("");
      setConfirmNewPassword("");
      // Pre-fill email on login form
      setEmail(resetEmail);
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao resetar senha";
      toast.error(message);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:grid lg:grid-cols-2" data-testid="login-page">
      {/* Mobile Image - Above form */}
      <div 
        className="lg:hidden h-40 sm:h-52 bg-cover bg-center bg-no-repeat relative"
        style={{ backgroundImage: `url(${AUTH_IMAGE})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white" />
      </div>
      
      {/* Desktop Image Side */}
      <div 
        className="hidden lg:block bg-cover bg-center"
        style={{ backgroundImage: `url(${AUTH_IMAGE})` }}
      />
      
      {/* Form Side */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-8 lg:mb-12">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
              data-testid="back-link"
            >
              <ArrowLeft size={16} />
              {t("back")}
            </Link>
            <LanguageSelector />
          </div>
          
          <div className="mb-8 lg:mb-12">
            <img src={LOGO_URL} alt="AESTHA" className="h-10 lg:h-12 invert mb-6 lg:mb-8" />
            <h1 className="font-serif text-2xl lg:text-3xl mb-2">{t("welcome_back")}</h1>
            <p className="text-sm lg:text-base text-muted-foreground">
              {t("enter_account")}
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-6">
            <div>
              <Label className="label-mono">{t("email")}</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-paper"
                placeholder="seu@email.com"
                required
                data-testid="email-input"
              />
            </div>
            
            <div>
              <Label className="label-mono">{t("password")}</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-paper pr-12"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <button
                type="button"
                onClick={() => setShowResetDialog(true)}
                className="text-sm text-muted-foreground hover:text-foreground mt-2"
                data-testid="forgot-password-link"
              >
                Esqueci minha senha
              </button>
            </div>
            
            <Button 
              type="submit" 
              className="btn-primary w-full"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? t("logging_in") : t("login")}
            </Button>
          </form>
          
          <p className="mt-6 lg:mt-8 text-center text-sm text-muted-foreground">
            {t("no_account")}{" "}
            <Link to="/register" className="text-foreground hover:underline" data-testid="register-link">
              {t("create_account")}
            </Link>
          </p>
        </div>
      </div>
      
      {/* Password Reset Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <KeyRound size={20} />
              Recuperar Senha
            </DialogTitle>
            <DialogDescription>
              Digite seu email e o código de recuperação que você recebeu do administrador.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleResetPassword} className="space-y-4 py-4">
            <div>
              <Label className="label-mono">Email</Label>
              <Input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="input-paper"
                placeholder="seu@email.com"
                required
                data-testid="reset-email-input"
              />
            </div>
            
            <div>
              <Label className="label-mono">Código de 6 dígitos</Label>
              <Input
                type="text"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="input-paper text-center text-xl tracking-widest font-mono"
                placeholder="000000"
                maxLength={6}
                required
                data-testid="reset-code-input"
              />
            </div>
            
            <div>
              <Label className="label-mono">Nova Senha</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input-paper"
                placeholder="Mínimo 6 caracteres"
                required
                data-testid="new-password-input"
              />
            </div>
            
            <div>
              <Label className="label-mono">Confirmar Nova Senha</Label>
              <Input
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                className="input-paper"
                placeholder="Digite novamente"
                required
                data-testid="confirm-new-password-input"
              />
            </div>
            
            <Button 
              type="submit" 
              className="btn-primary w-full"
              disabled={resetting || resetCode.length !== 6}
              data-testid="reset-password-submit"
            >
              {resetting ? "Alterando..." : "Alterar Senha"}
            </Button>
            
            <p className="text-xs text-muted-foreground text-center">
              Não tem um código? Entre em contato com o administrador para solicitar.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
